import { FormEvent, useEffect, useState } from "react";
import { api } from "../../api/client";
import { BottomNav } from "../../components/BottomNav";
import type { Cardholder, User } from "../../types";

export function AdminUsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [cardholders, setCardholders] = useState<Cardholder[]>([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [passwordUserId, setPasswordUserId] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordBusy, setPasswordBusy] = useState(false);

  async function reload() {
    const [u, c] = await Promise.all([
      api.request<User[]>("/users"),
      api.request<Cardholder[]>("/cardholders"),
    ]);
    setUsers(u);
    setCardholders(c);
  }

  useEffect(() => {
    reload().catch(console.error);
  }, []);

  async function createUser(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await api.request("/users", {
        method: "POST",
        body: JSON.stringify({
          email,
          full_name: name,
          password,
          role: "employee",
        }),
      });
      setMessage(`Created ${email}`);
      setEmail("");
      setName("");
      setPassword("");
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed");
    }
  }

  async function mapCardholder(cardholderId: number, userId: number | null) {
    await api.request(`/cardholders/${cardholderId}`, {
      method: "PATCH",
      body: JSON.stringify({ user_id: userId }),
    });
    await reload();
  }

  function openPasswordForm(userId: number) {
    setPasswordUserId(userId);
    setNewPassword("");
    setConfirmPassword("");
    setError(null);
    setMessage(null);
  }

  function cancelPasswordForm() {
    setPasswordUserId(null);
    setNewPassword("");
    setConfirmPassword("");
  }

  async function savePassword(e: FormEvent) {
    e.preventDefault();
    if (passwordUserId == null) return;
    setError(null);
    setMessage(null);
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setPasswordBusy(true);
    try {
      const target = users.find((u) => u.id === passwordUserId);
      await api.request(`/users/${passwordUserId}`, {
        method: "PATCH",
        body: JSON.stringify({ password: newPassword }),
      });
      setMessage(`Password updated for ${target?.full_name || "user"}`);
      cancelPasswordForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update password");
    } finally {
      setPasswordBusy(false);
    }
  }

  const passwordTarget = users.find((u) => u.id === passwordUserId) ?? null;

  return (
    <div className="app-shell admin">
      <header className="topbar">
        <h2 style={{ fontSize: "1.35rem" }}>People & cards</h2>
      </header>

      <div className="admin-grid">
        <form className="panel stack" onSubmit={createUser}>
          <h3 style={{ margin: 0, fontSize: "1.05rem" }}>Add employee</h3>
          <div className="field">
            <label>Full name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="field">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label>Temporary password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              required
            />
          </div>
          {error && <div className="alert">{error}</div>}
          {message && <div className="alert alert-ok">{message}</div>}
          <button className="btn btn-primary" type="submit">
            Create account
          </button>

          <h3 style={{ margin: "0.5rem 0 0", fontSize: "1.05rem" }}>Users</h3>
          {users.map((u) => (
            <div key={u.id} className="stack" style={{ gap: "0.35rem" }}>
              <div className="row between" style={{ alignItems: "flex-start" }}>
                <div>
                  <strong>{u.full_name}</strong>
                  <div className="muted">{u.email}</div>
                </div>
                <div className="row" style={{ gap: "0.4rem" }}>
                  <span className="badge badge-suggested">{u.role}</span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => openPasswordForm(u.id)}
                  >
                    Password
                  </button>
                </div>
              </div>
            </div>
          ))}
        </form>

        <section className="panel stack">
          <h3 style={{ margin: 0, fontSize: "1.05rem" }}>Cardholder mapping</h3>
          <p className="muted" style={{ margin: 0 }}>
            Map names / last-4 from the bank CSV to employee accounts so they only see their own spend.
          </p>
          {cardholders.length === 0 && (
            <p className="muted">Import a CSV first — cardholders are created automatically.</p>
          )}
          {cardholders.map((ch) => (
            <div key={ch.id} className="stack" style={{ gap: "0.35rem" }}>
              <div className="row between">
                <div>
                  <strong>{ch.name_on_card}</strong>
                  <div className="muted">
                    {ch.card_last4 ? `••${ch.card_last4}` : "No last-4"}
                  </div>
                </div>
              </div>
              <select
                value={ch.user_id ?? ""}
                onChange={(e) =>
                  mapCardholder(
                    ch.id,
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
                style={{
                  borderRadius: 10,
                  border: "1px solid var(--line)",
                  padding: "0.55rem 0.7rem",
                  background: "#fff",
                }}
              >
                <option value="">Unassigned</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </section>
      </div>

      {passwordTarget && (
        <div
          className="panel stack"
          style={{ marginTop: "0.85rem" }}
          role="dialog"
          aria-label="Change password"
        >
          <h3 style={{ margin: 0, fontSize: "1.05rem" }}>
            Change password — {passwordTarget.full_name}
          </h3>
          <p className="muted" style={{ margin: 0, fontSize: "0.85rem" }}>
            {passwordTarget.email}
          </p>
          <form className="stack" onSubmit={savePassword}>
            <div className="field">
              <label>New password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={8}
                autoComplete="new-password"
                required
              />
            </div>
            <div className="field">
              <label>Confirm password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                minLength={8}
                autoComplete="new-password"
                required
              />
            </div>
            <div className="row" style={{ gap: "0.5rem" }}>
              <button
                className="btn btn-primary"
                type="submit"
                disabled={passwordBusy}
                style={{ flex: 1 }}
              >
                {passwordBusy ? "Saving…" : "Save password"}
              </button>
              <button
                className="btn btn-ghost"
                type="button"
                disabled={passwordBusy}
                onClick={cancelPasswordForm}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
